import { shortDayShortMonth } from 'utils/DateFormats';
import type { TripAccommodationStay } from '~/services/engine-ts';
import type { AccommodationCheckInOutDetails } from './AccommodationCard.types';

export function formatAccommodationDisplayTime(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  const match = /^(\d{1,2}):(\d{2})/.exec(trimmed);
  if (match) {
    return `${match[1].padStart(2, '0')}:${match[2]}`;
  }
  return trimmed;
}

export function formatAccommodationStayDate(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  try {
    return shortDayShortMonth(value.trim());
  } catch {
    return null;
  }
}

export function getAccommodationCheckInOutDetails(
  stay: TripAccommodationStay | undefined
): AccommodationCheckInOutDetails | null {
  if (!stay) return null;

  const acc = stay.accommodation as TripAccommodationStay['accommodation'] & {
    checkInAfter?: string | null;
    checkOutBefore?: string | null;
  };

  const stayLevel = stay as TripAccommodationStay & {
    checkInTime?: string | null;
    checkOutTime?: string | null;
    checkInNoEarlierThan?: string | null;
  };

  const checkInTime = formatAccommodationDisplayTime(
    acc.checkInTime ?? acc.checkInAfter ?? stayLevel.checkInTime ?? null
  );
  const checkOutTime = formatAccommodationDisplayTime(
    acc.checkOutTime ?? acc.checkOutBefore ?? stayLevel.checkOutTime ?? null
  );

  if (checkInTime || checkOutTime) {
    const checkInNoEarlierThan = formatAccommodationDisplayTime(
      acc.checkInNoEarlierThan ?? stayLevel.checkInNoEarlierThan ?? checkInTime
    );

    return {
      variant: 'times',
      checkInNoEarlierThan,
      checkInValue: checkInTime,
      checkOutValue: checkOutTime
    };
  }

  const checkInDate = formatAccommodationStayDate(stay.checkIn);
  const checkOutDate = formatAccommodationStayDate(stay.checkOut);

  if (!checkInDate && !checkOutDate) {
    return null;
  }

  return {
    variant: 'dates',
    checkInNoEarlierThan: null,
    checkInValue: checkInDate,
    checkOutValue: checkOutDate
  };
}
